import moment from "moment";

import {
  AuthenticationService as BaseAuthenticationService,
  BasicUserJwt,
} from "@egis-auth/client";
import { DecodedJwt } from "@egis-auth/jwt-utils";

import { utf8_to_b64, b64_to_utf8 } from "../../../utils";

export enum LocalStorageKey {
  Token = "ir-ujwt",
}

export enum SessionStorageKey {
  Matricule = "ir-user-matricule",
  PinCode = "ir-user-pin-code",
  KeepSessionOpen = "ir-keep-session-open",
}

class AuthenticationService<
  JwtType extends DecodedJwt = BasicUserJwt
> extends BaseAuthenticationService<JwtType> {
  /**
   * Check if the user is logged in.
   *
   * @returns `true` if the user is logged in, `false` if not.
   */
  public isLoggedIn(): boolean {
    try {
      this.getToken();
    } catch (error) {
      return false;
    }

    return true;
  }

  /**
   * Get the raw token in the localStorage.
   *
   * @returns The raw token.
   * @throws Will throw an error if the token is not found in the local storage.
   */
  public getRawToken(): string {
    const rawToken = localStorage.getItem(LocalStorageKey.Token);

    if (rawToken === null) {
      throw new Error(
        `No token found in the local storage with the following key: "${LocalStorageKey.Token}".`
      );
    }

    return rawToken;
  }

  /**
   * Check if the token is expired.
   *
   * @returns `true` if the token is expired, `false` if not.
   * @throws Will throw an error if the token is not found in the local storage.
   * @throws Will throw an error if the exp field of the token is `undefined`.
   * @throws Will throw an error if the exp field of the token is `null`.
   * @throws Will throw an error if the exp field of the token is not a number.
   */
  public isTokenExpired(): boolean {
    try {
      const token = this.getToken();

      if (token.exp === undefined) {
        throw new Error("The exp field of the token is undefined.");
      }

      if (token.exp === null) {
        throw new Error("The exp field of the token is null.");
      }

      if (typeof token.exp !== "number") {
        throw new Error("The exp field of the token is not a number.");
      }

      return moment.unix(token.exp).utc().isBefore(Date.now());
    } catch (error) {
      throw error;
    }
  }

  /**
   * Set the user credentials in the session storage.
   *
   * @throws Throws a "QuotaExceededError" DOMException exception if
   * the new value couldn't be set. (Setting could fail if, e.g.,
   * the user has disabled storage for the site, or if the quota
   * has been exceeded.)
   */
  public setUserCredentials(
    matricule: string,
    pinCode: string,
    keepSessionOpen: "0" | "1"
  ): void {
    sessionStorage.setItem(SessionStorageKey.Matricule, utf8_to_b64(matricule));
    sessionStorage.setItem(SessionStorageKey.PinCode, utf8_to_b64(pinCode));
    sessionStorage.setItem(SessionStorageKey.KeepSessionOpen, keepSessionOpen);
  }

  /**
   * Get the user credentials from the session storage.
   */
  public getUserCredentials(): {
    matricule: string | null;
    pinCode: string | null;
    keepSessionOpen: string | null;
  } {
    const matricule = sessionStorage.getItem(SessionStorageKey.Matricule);
    const pinCode = sessionStorage.getItem(SessionStorageKey.PinCode);
    const keepSessionOpen = sessionStorage.getItem(
      SessionStorageKey.KeepSessionOpen
    );

    return {
      matricule: matricule !== null ? b64_to_utf8(matricule) : matricule,
      pinCode: pinCode !== null ? b64_to_utf8(pinCode) : pinCode,
      keepSessionOpen,
    };
  }

  /**
   * Remove the user credentials from the session storage.
   */
  public removeUserCredentials(): void {
    sessionStorage.removeItem(SessionStorageKey.Matricule);
    sessionStorage.removeItem(SessionStorageKey.PinCode);
    sessionStorage.removeItem(SessionStorageKey.KeepSessionOpen);
  }
}

export default AuthenticationService;
